import { useState, useEffect, useRef, useCallback } from "react";
import { useNavigate } from "react-router-dom";
import { Html5Qrcode } from "html5-qrcode";
import Quagga from "@ericblade/quagga2";
import { playBeep, unlockBeep } from "../lib/beep";
import { enqueueScan, flushQueue, queueLength } from "../lib/offlineQueue";
import { Button } from "./ui/button";
import { Input } from "./ui/input";
import {
  AlertDialog,
  AlertDialogContent,
  AlertDialogHeader,
  AlertDialogTitle,
  AlertDialogDescription,
  AlertDialogFooter,
  AlertDialogCancel,
} from "./ui/alert-dialog";
import { Dialog, DialogContent, DialogHeader, DialogTitle } from "./ui/dialog";
import ThemeToggle from "./ThemeToggle";

const API = (process.env.REACT_APP_BACKEND_URL || "") + "/api";

function Scanner() {
  const navigate = useNavigate();
  const [user, setUser] = useState(null);
  const [counter, setCounter] = useState(0);
  const [recentCodes, setRecentCodes] = useState([]);
  const [lastScannedCode, setLastScannedCode] = useState(null);
  const [alert, setAlert] = useState(null);
  const [nextEnabled, setNextEnabled] = useState(false);
  const [showFinalizeModal, setShowFinalizeModal] = useState(false);
  const [showReviewModal, setShowReviewModal] = useState(false);
  const [reviewItems, setReviewItems] = useState([]);
  const [selectedForDelete, setSelectedForDelete] = useState(new Set());
  const [isScanning, setIsScanning] = useState(false);
  const [isScannerReady, setIsScannerReady] = useState(false);
  const [scannerPaused, setScannerPaused] = useState(false);
  const [scanMode, setScanMode] = useState("qr"); // "barcode" or "qr"
  const [manualCode, setManualCode] = useState("");
  const [isListening, setIsListening] = useState(false);
  const [pendingCount, setPendingCount] = useState(0);
  const [showDetails, setShowDetails] = useState(false);
  const [detailQuantity, setDetailQuantity] = useState(1);
  const [detailCategory, setDetailCategory] = useState("");
  const [detailLocation, setDetailLocation] = useState("");
  const [detailNote, setDetailNote] = useState("");
  const recognitionRef = useRef(null);

  const html5QrcodeRef = useRef(null);
  const quaggaActiveRef = useRef(false);
  const lastScannedCodeRef = useRef(null);
  const lastScanTimeRef = useRef(0);
  const scanModeRef = useRef("qr");
  const scannerPausedRef = useRef(false);
  const pendingReadsRef = useRef([]); // For barcode multi-read validation

  // Keep refs in sync
  useEffect(() => { scanModeRef.current = scanMode; }, [scanMode]);
  useEffect(() => { scannerPausedRef.current = scannerPaused; }, [scannerPaused]);

  useEffect(() => {
    const token = localStorage.getItem("qre_token");
    const authHeaders = { "Authorization": `Bearer ${token}` };

    const fetchUser = async () => {
      try {
        const response = await fetch(`${API}/auth/me`, { headers: authHeaders });
        if (response.ok) setUser(await response.json());
      } catch (error) { console.error('Error fetching user:', error); }
    };

    const fetchStats = async () => {
      try {
        const response = await fetch(`${API}/session-stats`, { headers: authHeaders });
        if (response.ok) {
          const data = await response.json();
          setCounter(data.barcode_count);
        }
      } catch (error) { console.error('Error fetching stats:', error); }
    };

    fetchUser();
    fetchStats();
  }, []);

  // === OFFLINE QUEUE SYNC ===
  // Scans that failed to reach the backend (no connection) are queued in
  // localStorage; retry them periodically and whenever connectivity returns.
  const trySyncQueue = useCallback(async () => {
    const token = localStorage.getItem("qre_token");
    if (!token) return;
    const { synced, remaining } = await flushQueue(API, token);
    setPendingCount(remaining);
    if (synced > 0) {
      try {
        const response = await fetch(`${API}/session-stats`, {
          headers: { "Authorization": `Bearer ${token}` }
        });
        if (response.ok) setCounter((await response.json()).barcode_count);
      } catch (e) { /* will retry on next tick */ }
    }
  }, []);

  useEffect(() => {
    setPendingCount(queueLength());
    trySyncQueue();
    window.addEventListener("online", trySyncQueue);
    const interval = setInterval(trySyncQueue, 15000);
    return () => {
      window.removeEventListener("online", trySyncQueue);
      clearInterval(interval);
    };
  }, [trySyncQueue]);

  // === BARCODE SCAN HANDLER ===
  const handleBarcodeDetected = useCallback(async (decodedText) => {
    const now = Date.now();
    if (isScanning || scannerPausedRef.current) return;
    if (lastScannedCodeRef.current === decodedText && (now - lastScanTimeRef.current) < 2000) return;

    setIsScanning(true);
    lastScannedCodeRef.current = decodedText;
    lastScanTimeRef.current = now;

    // PAUSE scanner
    setScannerPaused(true);
    if (scanModeRef.current === "barcode" && quaggaActiveRef.current) {
      Quagga.pause();
    } else if (scanModeRef.current === "qr" && html5QrcodeRef.current) {
      try { await html5QrcodeRef.current.pause(); } catch (e) {}
    }

    try {
      const token = localStorage.getItem("qre_token");
      const response = await fetch(`${API}/barcode`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json', 'Authorization': `Bearer ${token}` },
        body: JSON.stringify({
          barcode: decodedText,
          quantity: detailQuantity,
          category: detailCategory || null,
          location: detailLocation || null,
          note: detailNote || null
        })
      });

      if (response.ok) {
        const data = await response.json();
        setCounter(data.barcode_count);
        setLastScannedCode(decodedText);
        setNextEnabled(true);
        setRecentCodes(prev => [
          { code: decodedText, time: new Date().toLocaleTimeString(), dup: data.is_duplicate },
          ...prev
        ]);

        if (navigator.vibrate) navigator.vibrate(200);
        playBeep();

        setAlert({
          type: data.is_duplicate ? 'warning' : 'success',
          message: data.is_duplicate
            ? `Código duplicado: ${decodedText}. Guardado de todos modos.`
            : `Código escaneado: ${decodedText}`
        });
        setTimeout(() => setAlert(null), 3000);
        setDetailNote("");
      } else if (response.status === 402) {
        const data = await response.json().catch(() => ({}));
        setAlert({ type: 'warning', message: data.detail || 'Límite del plan alcanzado' });
      } else {
        setAlert({ type: 'warning', message: 'Error al guardar el código' });
      }
    } catch (error) {
      // Network failed (no connection) - queue locally instead of losing the scan
      console.error('Error saving barcode, queuing offline:', error);
      enqueueScan(decodedText, { quantity: detailQuantity, category: detailCategory, location: detailLocation, note: detailNote });
      setPendingCount(queueLength());
      setCounter(prev => prev + 1);
      setLastScannedCode(decodedText);
      setNextEnabled(true);
      setRecentCodes(prev => [
        { code: decodedText, time: new Date().toLocaleTimeString(), dup: false, pending: true },
        ...prev
      ]);
      if (navigator.vibrate) navigator.vibrate(200);
      setAlert({ type: 'warning', message: `Sin conexión: ${decodedText} guardado localmente, se sincronizará solo.` });
      setTimeout(() => setAlert(null), 3000);
      setDetailNote("");
    } finally {
      setIsScanning(false);
    }
  }, [isScanning, detailQuantity, detailCategory, detailLocation, detailNote]);

  // === QUAGGA (BARCODE) INIT ===
  const startQuagga = useCallback(async () => {
    stopAllScanners();
    pendingReadsRef.current = [];

    return new Promise((resolve) => {
      Quagga.init({
        inputStream: {
          name: "Live",
          type: "LiveStream",
          target: document.querySelector("#reader"),
          constraints: {
            facingMode: "environment",
            width: { ideal: 1280 },
            height: { ideal: 720 }
          }
        },
        decoder: {
          readers: ["code_128_reader"],
          multiple: false
        },
        locate: true,
        locator: {
          patchSize: "large",
          halfSample: false
        },
        frequency: 10
      }, (err) => {
        if (err) {
          console.error("Quagga init error:", err);
          setAlert({ type: 'warning', message: 'Error al iniciar cámara para códigos de barras' });
          resolve(false);
          return;
        }
        Quagga.start();
        quaggaActiveRef.current = true;
        setIsScannerReady(true);
        resolve(true);
      });

      Quagga.onDetected((result) => {
        if (!result || !result.codeResult || !result.codeResult.code) return;
        if (scannerPausedRef.current) return;

        // Calculate average error from decodedCodes
        const errors = result.codeResult.decodedCodes
          .filter(d => d.error !== undefined)
          .map(d => d.error);
        const avgError = errors.length > 0
          ? errors.reduce((a, b) => a + b, 0) / errors.length
          : 1;

        // Reject high-error reads (lower = better, threshold 0.25)
        if (avgError > 0.25) return;

        const code = result.codeResult.code;
        const reads = pendingReadsRef.current;
        reads.push(code);

        // Keep only last 5 reads
        if (reads.length > 5) reads.shift();

        // Count consecutive same reads from the end
        let consecutive = 0;
        for (let i = reads.length - 1; i >= 0; i--) {
          if (reads[i] === code) consecutive++;
          else break;
        }

        // Require 3 consecutive identical reads to accept
        if (consecutive >= 3) {
          pendingReadsRef.current = [];
          handleBarcodeDetected(code);
        }
      });
    });
  }, [handleBarcodeDetected]);

  // === HTML5-QRCODE (QR) INIT ===
  const startQrScanner = useCallback(async () => {
    stopAllScanners();

    try {
      const html5QrCode = new Html5Qrcode("reader");
      html5QrcodeRef.current = html5QrCode;

      const cameras = await Html5Qrcode.getCameras();
      if (!cameras || cameras.length === 0) throw new Error('No cameras found');

      let cameraId = cameras[0].id;
      const backCamera = cameras.find(camera =>
        camera.label.toLowerCase().includes('back') ||
        camera.label.toLowerCase().includes('rear') ||
        camera.label.toLowerCase().includes('environment')
      );
      if (backCamera) cameraId = backCamera.id;
      else if (cameras.length > 1) cameraId = cameras[cameras.length - 1].id;

      await html5QrCode.start(
        cameraId,
        {
          fps: 10,
          qrbox: function(viewfinderWidth, viewfinderHeight) {
            let minEdge = Math.min(viewfinderWidth, viewfinderHeight);
            return {
              width: Math.floor(minEdge * 0.5),
              height: Math.floor(minEdge * 0.8)
            };
          },
          aspectRatio: 1.0,
          formatsToSupport: [0], // QR_CODE
          experimentalFeatures: {
            useBarCodeDetectorIfSupported: true
          }
        },
        (decodedText) => { handleBarcodeDetected(decodedText); },
        () => {}
      );
      setIsScannerReady(true);
    } catch (error) {
      console.error('Error starting QR scanner:', error);
      setAlert({ type: 'warning', message: 'Error al iniciar cámara para QR' });
    }
  }, [handleBarcodeDetected]);

  // === STOP ALL SCANNERS ===
  const stopAllScanners = () => {
    if (quaggaActiveRef.current) {
      try { Quagga.stop(); } catch (e) {}
      Quagga.offDetected();
      quaggaActiveRef.current = false;
    }
    if (html5QrcodeRef.current) {
      try {
        const state = html5QrcodeRef.current.getState();
        if (state === 2 || state === 3) {
          html5QrcodeRef.current.stop().then(() => {
            if (html5QrcodeRef.current) html5QrcodeRef.current.clear();
            html5QrcodeRef.current = null;
          }).catch(() => { html5QrcodeRef.current = null; });
        } else {
          html5QrcodeRef.current.clear();
          html5QrcodeRef.current = null;
        }
      } catch (e) { html5QrcodeRef.current = null; }
    }
    setIsScannerReady(false);
  };

  // === START SCANNER ON MODE CHANGE ===
  useEffect(() => {
    const timer = setTimeout(() => {
      if (scanMode === "barcode") startQuagga();
      else startQrScanner();
    }, 500);

    return () => {
      clearTimeout(timer);
      stopAllScanners();
    };
  }, [scanMode]);

  // === SWITCH MODE ===
  const toggleMode = () => {
    setScanMode(prev => prev === "barcode" ? "qr" : "barcode");
    setScannerPaused(false);
    setNextEnabled(false);
    lastScannedCodeRef.current = null;
  };

  // === SPEECH TO TEXT ===
  const toggleListening = () => {
    const SpeechRecognition = window.SpeechRecognition || window.webkitSpeechRecognition;
    if (!SpeechRecognition) {
      setAlert({ type: 'warning', message: 'Tu navegador no soporta dictado por voz' });
      setTimeout(() => setAlert(null), 3000);
      return;
    }

    if (isListening && recognitionRef.current) {
      recognitionRef.current.stop();
      setIsListening(false);
      return;
    }

    const recognition = new SpeechRecognition();
    recognitionRef.current = recognition;
    recognition.lang = 'es-CL';
    recognition.continuous = false;
    recognition.interimResults = false;

    recognition.onstart = () => setIsListening(true);

    recognition.onresult = (event) => {
      let transcript = event.results[0][0].transcript.trim();
      // Clean up: remove spaces, uppercase for codes
      transcript = transcript.replace(/\s+/g, '').toUpperCase();
      setManualCode(prev => prev + transcript);
    };

    recognition.onerror = () => setIsListening(false);
    recognition.onend = () => setIsListening(false);

    recognition.start();
  };

  // === MANUAL CODE ENTRY ===
  const handleManualSubmit = async () => {
    const code = manualCode.trim();
    if (!code) return;

    const token = localStorage.getItem("qre_token");
    try {
      const response = await fetch(`${API}/barcode`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json', 'Authorization': `Bearer ${token}` },
        body: JSON.stringify({
          barcode: code,
          quantity: detailQuantity,
          category: detailCategory || null,
          location: detailLocation || null,
          note: detailNote || null
        })
      });
      if (response.ok) {
        const data = await response.json();
        setCounter(data.barcode_count);
        setLastScannedCode(code);
        setNextEnabled(true);
        setRecentCodes(prev => [
          { code, time: new Date().toLocaleTimeString(), dup: data.is_duplicate },
          ...prev
        ]);
        if (navigator.vibrate) navigator.vibrate(200);
        playBeep();
        setAlert({
          type: data.is_duplicate ? 'warning' : 'success',
          message: data.is_duplicate
            ? `Código duplicado: ${code}. Guardado de todos modos.`
            : `Código ingresado: ${code}`
        });
        setTimeout(() => setAlert(null), 3000);
        setManualCode("");
        setDetailNote("");
      } else if (response.status === 402) {
        const data = await response.json().catch(() => ({}));
        setAlert({ type: 'warning', message: data.detail || 'Límite del plan alcanzado' });
      }
    } catch (error) {
      // Network failed (no connection) - queue locally instead of losing the code
      enqueueScan(code, { quantity: detailQuantity, category: detailCategory, location: detailLocation, note: detailNote });
      setPendingCount(queueLength());
      setCounter(prev => prev + 1);
      setLastScannedCode(code);
      setNextEnabled(true);
      setRecentCodes(prev => [
        { code, time: new Date().toLocaleTimeString(), dup: false, pending: true },
        ...prev
      ]);
      setAlert({ type: 'warning', message: `Sin conexión: ${code} guardado localmente, se sincronizará solo.` });
      setTimeout(() => setAlert(null), 3000);
      setManualCode("");
      setDetailNote("");
    }
  };

  // === ACTIONS ===
  const handleDelete = async () => {
    const token = localStorage.getItem("qre_token");
    try {
      const response = await fetch(`${API}/barcode-delete-last`, {
        method: 'POST',
        headers: { 'Authorization': `Bearer ${token}` }
      });
      if (response.ok) {
        const data = await response.json();
        setCounter(data.barcode_count);
        setRecentCodes(prev => prev.slice(1));
        setAlert({ type: 'warning', message: `Eliminado: ${data.removed}` });
        setTimeout(() => setAlert(null), 2000);
        if (data.barcode_count === 0) resumeScanner();
      }
    } catch (e) { console.error(e); }
  };

  const handleRepeat = async () => {
    const token = localStorage.getItem("qre_token");
    try {
      const response = await fetch(`${API}/barcode-repeat-last`, {
        method: 'POST',
        headers: { 'Authorization': `Bearer ${token}` }
      });
      if (response.ok) {
        const data = await response.json();
        setCounter(data.barcode_count);
        setRecentCodes(prev => [
          { code: data.repeated, time: new Date().toLocaleTimeString(), dup: true },
          ...prev
        ]);
        if (navigator.vibrate) navigator.vibrate(100);
        playBeep();
        setAlert({ type: 'success', message: `Repetido: ${data.repeated}` });
        setTimeout(() => setAlert(null), 2000);
      }
    } catch (e) { console.error(e); }
  };

  const resumeScanner = async () => {
    setNextEnabled(false);
    setLastScannedCode(null);
    lastScannedCodeRef.current = null;
    pendingReadsRef.current = [];
    unlockBeep();
    setScannerPaused(false);

    if (scanMode === "barcode" && quaggaActiveRef.current) {
      try {
        Quagga.start();
      } catch (e) {
        // If Quagga can't resume, reinitialize
        startQuagga();
      }
    } else if (scanMode === "qr" && html5QrcodeRef.current) {
      try { await html5QrcodeRef.current.resume(); } catch (e) {}
    }
  };

  const handleOpenReview = async () => {
    const token = localStorage.getItem("qre_token");
    try {
      const response = await fetch(`${API}/barcode-list`, {
        headers: { 'Authorization': `Bearer ${token}` }
      });
      if (response.ok) {
        const data = await response.json();
        setReviewItems(data.items);
        setSelectedForDelete(new Set());
        setShowReviewModal(true);
      }
    } catch (e) { console.error(e); }
  };

  const toggleSelectItem = (index) => {
    setSelectedForDelete(prev => {
      const next = new Set(prev);
      if (next.has(index)) next.delete(index);
      else next.add(index);
      return next;
    });
  };

  const handleDeleteSelected = async () => {
    if (selectedForDelete.size === 0) return;
    const token = localStorage.getItem("qre_token");
    try {
      const response = await fetch(`${API}/barcode-delete-selected`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json', 'Authorization': `Bearer ${token}` },
        body: JSON.stringify({ indices: Array.from(selectedForDelete) })
      });
      if (response.ok) {
        const data = await response.json();
        setCounter(data.barcode_count);
        setSelectedForDelete(new Set());
        setAlert({ type: 'success', message: `Eliminados: ${data.removed_count} código(s)` });
        setTimeout(() => setAlert(null), 2500);
        const listResp = await fetch(`${API}/barcode-list`, {
          headers: { 'Authorization': `Bearer ${token}` }
        });
        if (listResp.ok) {
          const listData = await listResp.json();
          setReviewItems(listData.items);
        }
        setRecentCodes(prev => {
          const remaining = data.barcodes || [];
          return remaining.slice(-5).reverse().map(code => ({ code, time: '', dup: false }));
        });
      }
    } catch (e) { console.error(e); }
  };

  const handleFinalize = () => {
    if (counter === 0) {
      setAlert({ type: 'warning', message: 'No hay códigos escaneados para finalizar' });
      setTimeout(() => setAlert(null), 3000);
      return;
    }
    setShowFinalizeModal(true);
  };

  const confirmFinalize = async () => {
    try {
      const token = localStorage.getItem("qre_token");
      const response = await fetch(`${API}/finalize-session`, {
        method: 'POST',
        headers: { 'Authorization': `Bearer ${token}` }
      });
      if (response.ok) {
        const data = await response.json();
        setAlert({ type: 'success', message: `Sesión finalizada. Archivo: ${data.csv_filename}` });
        setShowFinalizeModal(false);
        setTimeout(() => {
          setCounter(0);
          setNextEnabled(false);
          setLastScannedCode(null);
          setRecentCodes([]);
          setAlert(null);
        }, 3000);
      } else {
        setAlert({ type: 'warning', message: 'Error al finalizar la sesión' });
      }
    } catch (error) {
      console.error('Error finalizing session:', error);
      setAlert({ type: 'warning', message: 'Error de conexión' });
    }
    setShowFinalizeModal(false);
  };

  const handleLogout = async () => {
    try {
      const token = localStorage.getItem("qre_token");
      await fetch(`${API}/auth/logout`, {
        method: 'POST',
        headers: { 'Authorization': `Bearer ${token}` }
      });
    } catch (error) { console.error('Error logging out:', error); }
    localStorage.removeItem("qre_token");
    localStorage.removeItem("qre_email");
    navigate('/login');
  };

  return (
    <div className="scanner-container">
      <div className="scanner-header">
        <div className="flex items-center justify-between gap-2.5">
          <div className="flex items-center gap-2.5">
            <img src="/logo.svg" alt="" className="brand-logo-sm" />
            <h1>Escáner QR / Barras</h1>
          </div>
          <ThemeToggle className="text-white hover:bg-white/20 hover:text-white shrink-0" />
        </div>
        {user && (
          <div className="user-info">
            <span className="user-name">{user.email}</span>
            <button className="logout-button" onClick={handleLogout} data-testid="logout-button">
              Salir
            </button>
          </div>
        )}
      </div>

      <div className="counter-card">
        <div className="counter-label">Lecturas válidas</div>
        <div className="counter-value" data-testid="barcode-counter">{counter}</div>
        {pendingCount > 0 && (
          <div data-testid="pending-sync-badge" className="mt-2 text-xs text-destructive bg-[#FCEBEB] dark:bg-[#371B1B] rounded-md px-2.5 py-1.5 inline-block">
            ⏳ {pendingCount} código(s) sin sincronizar (sin conexión)
          </div>
        )}
      </div>

      {/* MODE TOGGLE */}
      <div className="flex mx-4 my-2 rounded-lg overflow-hidden border-2 border-primary">
        <button
          onClick={() => scanMode !== "qr" && toggleMode()}
          data-testid="mode-qr"
          className={`flex-1 py-2.5 text-sm font-bold ${scanMode === "qr" ? "bg-primary text-primary-foreground" : "bg-card text-primary"}`}
        >
          Código QR
        </button>
        <button
          onClick={() => scanMode !== "barcode" && toggleMode()}
          data-testid="mode-barcode"
          className={`flex-1 py-2.5 text-sm font-bold ${scanMode === "barcode" ? "bg-primary text-primary-foreground" : "bg-card text-primary"}`}
        >
          Código de Barras
        </button>
      </div>

      {/* MANUAL CODE ENTRY */}
      <div className="flex mx-4 my-2 gap-2">
        <Input
          type="text"
          value={manualCode}
          onChange={(e) => setManualCode(e.target.value)}
          onKeyDown={(e) => e.key === 'Enter' && handleManualSubmit()}
          placeholder="Ingresar código manual..."
          data-testid="manual-code-input"
          className={`flex-1 h-11 text-sm border-2 ${isListening ? 'border-destructive' : ''}`}
        />
        <Button
          type="button"
          onClick={toggleListening}
          data-testid="voice-input-button"
          variant={isListening ? "destructive" : "secondary"}
          className={`h-11 w-11 p-0 text-lg shrink-0 ${isListening ? 'animate-pulse' : ''}`}
        >
          {isListening ? '...' : '\u{1F3A4}'}
        </Button>
        <Button
          onClick={handleManualSubmit}
          disabled={!manualCode.trim()}
          data-testid="manual-code-submit"
          variant="brand"
          className="h-11 px-4 shrink-0"
        >
          Agregar
        </Button>
      </div>

      {/* OPTIONAL DETAILS (quantity/category/location/note) - applies to the next scan */}
      <div className="mx-4 mb-2">
        <button
          onClick={() => setShowDetails(prev => !prev)}
          data-testid="toggle-details-button"
          className="bg-transparent border-none text-primary text-[13px] font-bold cursor-pointer py-1"
        >
          {showDetails ? '▲ Ocultar detalles' : '▼ Detalles (cantidad, categoría, ubicación, nota)'}
        </button>
        {showDetails && (
          <div className="grid grid-cols-2 gap-2 bg-card p-3 rounded-lg border border-border">
            <Input
              type="number" min="1" value={detailQuantity}
              onChange={(e) => setDetailQuantity(Math.max(1, parseInt(e.target.value, 10) || 1))}
              placeholder="Cantidad" data-testid="detail-quantity-input"
              className="h-9 text-[13px]"
            />
            <Input
              type="text" value={detailCategory} onChange={(e) => setDetailCategory(e.target.value)}
              placeholder="Categoría" data-testid="detail-category-input"
              className="h-9 text-[13px]"
            />
            <Input
              type="text" value={detailLocation} onChange={(e) => setDetailLocation(e.target.value)}
              placeholder="Ubicación / bodega" data-testid="detail-location-input"
              className="h-9 text-[13px]"
            />
            <Input
              type="text" value={detailNote} onChange={(e) => setDetailNote(e.target.value)}
              placeholder="Nota (se limpia tras cada escaneo)" data-testid="detail-note-input"
              className="h-9 text-[13px]"
            />
          </div>
        )}
      </div>

      {alert && (
        <div className={`alert alert-${alert.type}`} data-testid="alert-message">
          <span>{alert.message}</span>
        </div>
      )}

      <div className="scanner-card">
        <div id="reader" className="w-full max-w-[500px] mx-auto overflow-hidden rounded-lg">
          {/* Quagga renders video here for barcode mode, html5-qrcode for QR mode */}
        </div>
        {!isScannerReady && (
          <div className="text-center p-5 text-muted-foreground text-sm">
            <p>Inicializando escáner...</p>
            <p>Permite el acceso a la cámara cuando se solicite</p>
          </div>
        )}
        {isScannerReady && (
          <div className={`text-center p-3 text-[13px] rounded-lg mt-2 text-foreground ${scannerPaused ? 'bg-[#FCEBEB] dark:bg-[#371B1B] border-2 border-destructive' : 'bg-accent'}`}>
            {scannerPaused ? (
              <><strong>Escáner PAUSADO</strong><br />Presiona "Próximo" para continuar</>
            ) : (
              <strong>
                {scanMode === "barcode" ? "Apunta al código de barras" : "Apunta al código QR"}
              </strong>
            )}
          </div>
        )}
      </div>

      {recentCodes.length > 0 && (
        <div data-testid="recent-codes-box" className="mx-4 my-2 p-3 bg-card rounded-lg border border-border max-h-[150px] overflow-y-auto">
          <div className="font-bold text-primary mb-2 text-sm">
            Últimos registros
          </div>
          {recentCodes.slice(0, 5).map((item, i) => (
            <div
              key={i}
              className={`flex justify-between py-1 text-[13px] ${i < Math.min(recentCodes.length, 5) - 1 ? 'border-b border-border' : ''} ${item.dup ? 'text-destructive' : 'text-foreground'}`}
            >
              <span className="flex-1 overflow-hidden text-ellipsis whitespace-nowrap mr-2">
                {item.pending && '⏳ '}{item.code}
              </span>
              <span className="text-muted-foreground shrink-0">{item.time}</span>
            </div>
          ))}
        </div>
      )}

      <div className="fixed bottom-0 left-0 right-0 bg-card px-4 pt-3 pb-4 shadow-[0_-4px_12px_rgba(12,68,124,0.15)]">
        <div className="flex gap-2 mb-2">
          <Button
            disabled={!nextEnabled} onClick={handleDelete} data-testid="delete-button"
            className={`flex-1 h-auto py-3.5 text-sm font-bold text-white disabled:opacity-100 ${nextEnabled ? 'bg-[#e57373] hover:bg-[#d96565]' : 'bg-[#ccc] dark:bg-[#3a4149]'}`}
          >
            Borrar
          </Button>
          <Button
            disabled={!nextEnabled} onClick={() => { setAlert(null); resumeScanner(); }} data-testid="next-button"
            className={`flex-[2] h-auto py-3.5 text-sm font-bold text-white disabled:opacity-100 ${nextEnabled ? 'bg-[#81c784] hover:bg-[#72bb74]' : 'bg-[#ccc] dark:bg-[#3a4149]'}`}
          >
            Próximo
          </Button>
          <Button
            disabled={!nextEnabled} onClick={handleRepeat} data-testid="repeat-button"
            className={`flex-1 h-auto py-3.5 text-sm font-bold text-white disabled:opacity-100 ${nextEnabled ? 'bg-[#64b5f6] hover:bg-[#4fa8f5]' : 'bg-[#ccc] dark:bg-[#3a4149]'}`}
          >
            Repetir
          </Button>
        </div>
        <div className="flex gap-2">
          <Button
            variant="secondary" onClick={handleOpenReview} data-testid="review-button" disabled={counter === 0}
            className="flex-1 h-auto py-3 text-sm"
          >
            Revisar ({counter})
          </Button>
          <Button variant="destructive" onClick={handleFinalize} data-testid="finalize-button" className="flex-1 h-auto py-3 text-sm">
            Finalizar
          </Button>
          {localStorage.getItem("qre_admin") === "1" && (
            <Button variant="brand" onClick={() => navigate("/downloads")} data-testid="downloads-button" className="flex-1 h-auto py-3 text-sm">
              Descargas
            </Button>
          )}
        </div>
      </div>

      {/* FINALIZE MODAL */}
      <AlertDialog open={showFinalizeModal} onOpenChange={setShowFinalizeModal}>
        <AlertDialogContent data-testid="finalize-modal">
          <AlertDialogHeader>
            <AlertDialogTitle>¿Finalizar sesión?</AlertDialogTitle>
            <AlertDialogDescription asChild>
              <div>
                <strong className="text-foreground">Total de códigos: {counter}</strong>
                <br /><br />
                Se generará un archivo CSV con todos los códigos escaneados. ¿Deseas continuar?
              </div>
            </AlertDialogDescription>
          </AlertDialogHeader>
          <AlertDialogFooter>
            <AlertDialogCancel data-testid="cancel-finalize-button">Cancelar</AlertDialogCancel>
            <Button variant="destructive" onClick={confirmFinalize} data-testid="confirm-finalize-button">Sí, finalizar</Button>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>

      {/* REVIEW MODAL */}
      <Dialog open={showReviewModal} onOpenChange={setShowReviewModal}>
        <DialogContent className="max-w-[500px] max-h-[85vh] flex flex-col p-0 gap-0" data-testid="review-modal">
          <DialogHeader className="p-4 border-b border-border space-y-0">
            <DialogTitle className="text-lg text-primary text-left">Revisar códigos ({reviewItems.length})</DialogTitle>
          </DialogHeader>
          <div className="p-3 border-b border-border flex gap-2 items-center">
            <span className="text-[13px] text-muted-foreground">
              {selectedForDelete.size > 0 ? `${selectedForDelete.size} seleccionado(s)` : 'Marca los que deseas eliminar'}
            </span>
            {selectedForDelete.size > 0 && (
              <Button variant="destructive" size="sm" className="ml-auto" onClick={handleDeleteSelected} data-testid="delete-selected-button">
                Eliminar ({selectedForDelete.size})
              </Button>
            )}
          </div>
          <div className="flex-1 overflow-y-auto p-2 px-3">
            {reviewItems.length === 0 ? (
              <p className="text-center text-muted-foreground p-5">No hay códigos escaneados</p>
            ) : (
              reviewItems.map((item) => (
                <div
                  key={item.index} onClick={() => toggleSelectItem(item.index)} data-testid={`review-item-${item.index}`}
                  className={`flex items-center p-2.5 px-2 rounded-md mb-0.5 cursor-pointer ${selectedForDelete.has(item.index) ? 'bg-destructive/10' : ''}`}
                >
                  <div className={`w-[22px] h-[22px] rounded mr-3 shrink-0 flex items-center justify-center border-2 ${selectedForDelete.has(item.index) ? 'border-destructive bg-destructive' : 'border-muted-foreground'}`}>
                    {selectedForDelete.has(item.index) && <span className="text-white text-sm font-bold">✓</span>}
                  </div>
                  <div className="flex-1 overflow-hidden">
                    <div className={`text-sm font-medium overflow-hidden text-ellipsis whitespace-nowrap ${selectedForDelete.has(item.index) ? 'text-destructive line-through' : 'text-foreground'}`}>
                      {item.barcode}
                    </div>
                    <div className="text-[11px] text-muted-foreground">#{item.index + 1} · {item.timestamp}</div>
                  </div>
                </div>
              ))
            )}
          </div>
        </DialogContent>
      </Dialog>
    </div>
  );
}

export default Scanner;
